import { FILE_SERVICE_BASE_URL } from "../../constant/apiUrl";

/**
 * tus-js-client `urlStorage` backed by content-service's `/upload/tus/resume` endpoints
 * instead of `localStorage`, so resume works across browsers and logins, not just within one
 * browser profile. Same interface as `WebStorageUrlStorage`:
 *   findAllUploads()               -> Promise<Array<upload>>
 *   findUploadsByFingerprint(fp)   -> Promise<Array<upload>>
 *   removeUpload(urlStorageKey)    -> Promise<void>
 *   addUpload(fingerprint, upload) -> Promise<urlStorageKey>
 *
 * The fingerprint (computed in tusUpload.js: user + filename + type + size + lastModified) is
 * hashed server-side into the storage key, so the same file resolves to the same pointer
 * regardless of which browser/login asks.
 *
 * Every method fails soft — a lookup error just means "start fresh," and a store/cleanup
 * error shouldn't fail an otherwise-successful upload.
 */

const RESUME_ENDPOINT = `${FILE_SERVICE_BASE_URL}/fritolay/upload/tus/resume`;

// Duplicated from tusUpload.js rather than imported, to avoid a circular import.
const getAuthHeader = () => {
  try {
    const token = localStorage.getItem("auth_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
};

export const tusResumeStorage = {
  // Not used by our resume flow — kept as a no-op just to match the expected interface.
  findAllUploads() {
    return Promise.resolve([]);
  },

  findUploadsByFingerprint(fingerprint) {
    return fetch(`${RESUME_ENDPOINT}?fingerprint=${encodeURIComponent(fingerprint)}`, {
      method: "GET",
      headers: { ...getAuthHeader() },
    })
      .then((res) => {
        if (res.status === 204 || res.status === 404) return [];
        if (!res.ok) throw new Error(`resume lookup failed: ${res.status}`);
        return res.json().then(({ urlStorageKey, payload }) => {
          const upload = JSON.parse(payload);
          upload.urlStorageKey = urlStorageKey;
          return [upload];
        });
      })
      .catch((err) => {
        console.warn("tus resume lookup failed, starting fresh:", err);
        return [];
      });
  },

  removeUpload(urlStorageKey) {
    return fetch(`${RESUME_ENDPOINT}/${encodeURIComponent(urlStorageKey)}`, {
      method: "DELETE",
      headers: { ...getAuthHeader() },
    }).catch((err) => {
      console.warn("tus resume pointer cleanup failed (non-fatal):", err);
    });
  },

  addUpload(fingerprint, upload) {
    return fetch(`${RESUME_ENDPOINT}?fingerprint=${encodeURIComponent(fingerprint)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...getAuthHeader() },
      body: JSON.stringify(upload),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`resume store failed: ${res.status}`);
        return res.json().then(({ urlStorageKey }) => urlStorageKey);
      })
      .catch((err) => {
        console.warn("tus resume pointer store failed (non-fatal):", err);
        return null;
      });
  },
};

export default tusResumeStorage;
